// REC7-S1 (docs/build-briefs/REC7-S1-franchisee-publishes-own-recipes.md; DEC-423 PROVISIONAL; DEC-339, P0.5): the pure
// decisions behind the franchisee's "Publish to my locations" area in Settings > Recipes. No DOM, no fetch:
// test/rec7-s1-franchisee-publish-ui-model.test.ts proves it.
//
// The draft is the franchisor console's recipe draft (a name, a category and descriptive ingredients), so the request has no
// field that could hold an inventory item: the franchisee maps its own items later, in the Map ingredients step. An own
// recipe takes no Optional or Required choice and no `adoption` key. The locations are the franchisee's own, A to Z.

import {
  emptyRecipeDraft,
  RECIPE_LOCATIONS_MISSING,
  recipeDraftToBody,
  type RecipeDraft,
  type RecipeDraftField,
} from "./franchisorRecipeModel.ts";
import { sortByName } from "./nameSort.ts";

export {
  emptyRecipeDraft,
  emptySlot,
  parseSlotAmount,
  type RecipeDraft,
} from "./franchisorRecipeModel.ts";

export interface OwnLocationDto {
  id: string;
  name: string;
}

export interface OwnRecipePublicationsDto {
  locations: OwnLocationDto[];
  publications: unknown[];
}

export function isOwnRecipePublicationsShape(value: unknown): value is OwnRecipePublicationsDto {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v["publications"]) &&
    Array.isArray(v["locations"]) &&
    (v["locations"] as unknown[]).every((b) => {
      if (typeof b !== "object" || b === null) return false;
      const siteLocation = b as Record<string, unknown>;
      return typeof siteLocation["id"] === "string" && typeof siteLocation["name"] === "string";
    })
  );
}

/** The franchisee's locations A to Z (DEC-339), whatever order the server sent; the input is not mutated. */
export function ownLocationChoices(locations: readonly OwnLocationDto[]): OwnLocationDto[] {
  return sortByName(locations, (b) => b.name);
}

export type OwnDraftResult =
  | { ok: true; body: Record<string, unknown> }
  | { ok: false; field: RecipeDraftField; message: string; slotIndex?: number };

/** The request body for an own recipe, or the first problem. The server checks everything again. */
export function ownRecipeDraftToBody(
  draft: RecipeDraft,
  selected: ReadonlySet<string>,
): OwnDraftResult {
  const made = recipeDraftToBody(draft, "optional", selected);
  if (!made.ok) return made;
  const { adoption: _adoption, ...body } = made.body;
  void _adoption;
  return { ok: true, body };
}

/** The form's error line after a location is ticked or cleared: the "choose a location" message goes once a location is ticked; any other
 * message stays until the next save. */
export function errorAfterLocationChange(message: string, ticked: number): string {
  return message === RECIPE_LOCATIONS_MISSING && ticked > 0 ? "" : message;
}

export const OWN_PUBLISH_FAILED_MESSAGE =
  "The recipe could not be published. Nothing was changed. Try again.";
export const OWN_PUBLISH_LOAD_FAILED =
  "Your locations could not be loaded. Check your connection and reopen this screen.";

/** Words for a failed publish. The server's sentence for a known refusal is plain language. */
export function describeOwnPublishFailure(status: number, body: unknown): string {
  const record = typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
  const code = record["error"];
  const message = record["message"];
  if (status === 0) return "Could not reach the server. Check your connection and try again.";
  if (status === 403) return "You do not have permission to publish recipes.";
  if (status === 404)
    return "One of those locations is no longer yours. Reopen this screen and choose again.";
  if (
    (code === "publication_name_taken" || code === "invalid_request") &&
    typeof message === "string" &&
    message !== ""
  ) {
    return message;
  }
  return OWN_PUBLISH_FAILED_MESSAGE;
}

export function publishedMessage(name: string): string {
  return `${name} published.`;
}

export { emptyRecipeDraft as emptyOwnDraft };
